import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Loader2, RefreshCw, Search, Heart } from 'lucide-react';
import { matchService, MatchedUser } from '../../services/match.service';
import MessageThread from './MessageThread';
import './Chat.css';

interface Conversation {
  id: string;
  userName: string;
  userAvatar: string;
  lastMessage: string;
  timestamp: string;
  unread?: boolean;
  userEmail: string;
}

const transformUserToConversation = (user: MatchedUser): Conversation => {
  const defaultAvatar = require('../img/user.png');

  return {
    id: user.userId,
    userName: user.firstName || 'Unknown User',
    userAvatar: user.profilePictureUrl || defaultAvatar,
    lastMessage: 'Start your conversation',
    timestamp: '',
    unread: false,
    userEmail: user.email
  };
};

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [showMobileThread, setShowMobileThread] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentAuthToken, setCurrentAuthToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [currentUserId, setCurrentUserId] = useState<string | null>(localStorage.getItem('userId'));
  const previousUserIdRef = useRef<string | null>(localStorage.getItem('userId'));

  const fetchMatches = async (useCache: boolean = true) => {
    const authToken = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');

    if (!authToken || !userId) {
      console.warn('⚠️ No auth token or user ID found, redirecting to login');
      navigate('/login');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      let matchedUsers;

      if (useCache) {
        const cachedMatches = matchService.getCachedMatches();
        if (cachedMatches && cachedMatches.length > 0) {
          console.log('📦 Using cached matches');
          matchedUsers = cachedMatches;
        }
      }

      if (!matchedUsers) {
        const response = await matchService.getMatchedUsers();

        if (response.success && response.data) {
          matchedUsers = response.data;
          console.log('✅ Matched users loaded:', matchedUsers.length);
        } else {
          setError(response.message || 'Failed to load matches');
          console.error('❌ Failed to fetch matches:', response.message);
          return;
        }
      }

      const transformedConversations = matchedUsers.map(transformUserToConversation);
      setConversations(transformedConversations);
    } catch (err: any) {
      console.error('❌ Error fetching matches:', err);
      setError('An error occurred while loading your matches');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');

    if (!authToken || !userId) {
      navigate('/login');
      return;
    }

    if (userId !== previousUserIdRef.current) {
      console.log('🔄 Immediate user change detected on mount');
      console.log('Previous userId:', previousUserIdRef.current);
      console.log('Current userId:', userId);

      previousUserIdRef.current = userId;
      setCurrentUserId(userId);
      setCurrentAuthToken(authToken);
      setSelectedConversation(null);
      setShowMobileThread(false);
      setConversations([]);
      setError(null);

      matchService.clearMatchCache();
      fetchMatches(false);
    } else {
      fetchMatches(false);
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const handleAuthChange = () => {
      const newAuthToken = localStorage.getItem('authToken');
      const newUserId = localStorage.getItem('userId');

      if (newUserId !== currentUserId || newAuthToken !== currentAuthToken) {
        console.log('🔄 Account switch detected, resetting chat state');

        setCurrentAuthToken(newAuthToken);
        setCurrentUserId(newUserId);
        setSelectedConversation(null);
        setShowMobileThread(false);
        setConversations([]);
        setError(null);
        setIsRefreshing(false);

        previousUserIdRef.current = newUserId;

        if (newAuthToken && newUserId) {
          matchService.clearMatchCache();
          fetchMatches(false);
        } else {
          navigate('/login');
        }
      }
    };

    const checkAuthOnInterval = setInterval(() => {
      handleAuthChange();
    }, 1000);

    return () => {
      clearInterval(checkAuthOnInterval);
    };
  }, [currentAuthToken, currentUserId, navigate]);

  useEffect(() => {
    const userId = searchParams.get('userId');

    if (userId && conversations.length > 0 && !isLoading) {
      const userExists = conversations.find(c => c.id === userId);

      if (userExists) {
        setSelectedConversation(userId);
        setShowMobileThread(true);

        setTimeout(() => {
          const conversationElement = document.querySelector(`[data-conversation-id="${userId}"]`);
          if (conversationElement) {
            conversationElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);

        console.log('✅ Auto-selected conversation for user:', userId);
      } else {
        console.warn('⚠️ User not found in conversations:', userId);
      }
    }
  }, [searchParams, conversations, isLoading]);

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversation(conversationId);
    setShowMobileThread(true);
  };

  const handleBackToList = () => {
    setShowMobileThread(false);
    setSelectedConversation(null);
  };

  const handleRetry = () => {
    matchService.clearMatchCache();
    fetchMatches(false);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    matchService.clearMatchCache();
    await fetchMatches(false);
  };

  const handleFindMatches = () => {
    navigate('/search');
  };

  const selectedConv = conversations.find(c => c.id === selectedConversation);

  return (
    <div className="chat-container">
      <div className={`conversations-panel ${showMobileThread ? 'hide-mobile' : ''}`}>
        <div className="chat-header">
          <button className="back-button-chat" onClick={() => navigate('/home')}>
            <ArrowLeft size={24} />
          </button>
          <h1 className="chat-title">
            Messages {conversations.length > 0 && `(${conversations.length})`}
          </h1>
          <button
            className="refresh-button-chat"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            title="Refresh matches"
          >
            <RefreshCw
              size={20}
              className={isRefreshing ? 'spinner' : ''}
            />
          </button>
        </div>

        <div className="conversations-list">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', flexDirection: 'column', gap: '16px' }}>
              <Loader2 size={48} className="spinner" style={{ animation: 'spin 1s linear infinite', color: '#007bff' }} />
              <p style={{ marginTop: '8px', color: '#666', fontSize: '16px', fontWeight: '500' }}>
                Finding your matches...
              </p>
              <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', maxWidth: '250px' }}>
                This may take a moment as we check for mutual connections
              </p>
            </div>
          ) : error ? (
            <div style={{ padding: '32px 24px', textAlign: 'center' }}>
              <p style={{ color: '#e74c3c', marginBottom: '20px', fontSize: '15px' }}>{error}</p>
              <button
                onClick={handleRetry}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500'
                }}
              >
                Try Again
              </button>
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
              <Heart size={80} style={{ color: '#ffc0cb', opacity: 0.6 }} />
              <div>
                <h3 style={{ color: '#333', marginBottom: '12px', fontSize: '20px' }}>No matches yet</h3>
                <p style={{ color: '#666', fontSize: '15px', lineHeight: '1.5', maxWidth: '280px', margin: '0 auto' }}>
                  Start exploring profiles and liking people to find your matches!
                </p>
              </div>
              <button
                onClick={handleFindMatches}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '8px'
                }}
              >
                <Search size={18} />
                Find People to Like
              </button>
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation.id}
                data-conversation-id={conversation.id}
                className={`conversation-item ${selectedConversation === conversation.id ? 'active' : ''}`}
                onClick={() => handleConversationClick(conversation.id)}
              >
                <div className="conversation-avatar">
                  <img src={conversation.userAvatar} alt={conversation.userName} />
                  {conversation.unread && <span className="unread-badge"></span>}
                </div>
                <div className="conversation-info">
                  <div className="conversation-header-row">
                    <h3 className="conversation-name">{conversation.userName}</h3>
                    <span className="conversation-time">{conversation.timestamp}</span>
                  </div>
                  <p className="conversation-preview">{conversation.lastMessage}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={`messages-panel ${showMobileThread ? 'show-mobile' : ''}`}>
        {selectedConv ? (
          <>
            <div className="messages-header">
              <button className="back-button-mobile" onClick={handleBackToList}>
                <ArrowLeft size={24} />
              </button>
              <div className="messages-header-user">
                <img src={selectedConv.userAvatar} alt={selectedConv.userName} className="header-avatar" />
                <h2 className="header-user-name">{selectedConv.userName}</h2>
              </div>
            </div>

            <MessageThread
              receiverId={selectedConv.id}
              receiverName={selectedConv.userName}
              receiverAvatar={selectedConv.userAvatar}
              receiverEmail={selectedConv.userEmail}
            />
          </>
        ) : (
          <div className="no-conversation-selected">
            <MessageSquare size={80} className="empty-icon" />
            <h2 className="empty-title">Select a conversation</h2>
            <p className="empty-description">Choose a conversation from the list to start messaging</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;